/**
 * FormContainerPropertyPanel Component
 * 7-panel property editor for FormContainer design properties
 * Uses accordion layout with collapsible sections
 */

import React, { useState } from 'react';
import { ChildFormsGridPanel } from './ChildFormsGridPanel';
import { FormContainerConfig } from '../types/index';
import './FormContainerPropertyPanel.css';
import { ChevronDownIcon, ChevronRightIcon } from '@atlas-forms/ui-components-react';

// Try to import icons from designer-components-react, fallback to basic SVG if not available
let ContainerBasicIcon: any = null;
let ContainerLayoutIcon: any = null;
let ChildFormsIcon: any = null;
let BindingIcon: any = null;
let ValidationIcon: any = null;
let SaveIcon: any = null;
let NavigationIcon: any = null;

try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const iconSet = (globalThis as any).require?.('@atlas-forms/designer-components-react/dist/components/Icons/PropertiesIconSet') ?? {};
  ContainerBasicIcon = iconSet.ContainerBasicIcon;
  ContainerLayoutIcon = iconSet.ContainerLayoutIcon;
  ChildFormsIcon = iconSet.ChildFormsIcon;
  BindingIcon = iconSet.BindingIcon;
  ValidationIcon = iconSet.ValidationIcon;
  SaveIcon = iconSet.SaveIcon;
  NavigationIcon = iconSet.NavigationIcon;
} catch (e) {
  // Icons not available, will use no icon
}

export interface FormContainerPropertyPanelProps {
  control: any; // FormControl type
  onChange: (control: any) => void;
}

type PanelType = 'basic' | 'layout' | 'childForms' | 'binding' | 'validation' | 'save' | 'navigation';

const PANELS: PanelType[] = ['basic', 'layout', 'childForms', 'binding', 'validation', 'save', 'navigation'];

const PANEL_LABELS: Record<PanelType, { title: string; subtitle: string }> = {
  basic: {
    title: 'Container Basics',
    subtitle: 'Container ID, label, and general settings',
  },
  layout: {
    title: 'Container Layout',
    subtitle: 'Choose layout type and configure dimensions',
  },
  childForms: {
    title: 'Child Forms',
    subtitle: 'Add, configure, and manage child forms',
  },
  binding: {
    title: 'Data Binding',
    subtitle: 'Configure data binding and sync strategy',
  },
  validation: {
    title: 'Validation Rules',
    subtitle: 'Set validation mode and error handling',
  },
  save: {
    title: 'Save Strategy',
    subtitle: 'Configure where and how data is saved',
  },
  navigation: {
    title: 'Navigation Controls',
    subtitle: 'Control buttons, progress, and navigation behavior',
  },
};

// ─── AccordionSection Component ───────────────────────────────────────────

interface AccordionSectionProps {
  title: string;
  subtitle: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  icon?: React.ReactNode;
}

function AccordionSection({ title, subtitle, isOpen, onToggle, children, icon }: AccordionSectionProps) {
  return (
    <div className="af-form-container__accordion-section">
      <button
        className={`af-form-container__accordion-header ${isOpen ? 'af-form-container__accordion-header--open' : ''}`}
        onClick={onToggle}
        type="button"
      >
        <span className="af-form-container__accordion-toggle">
          {isOpen ? <ChevronDownIcon width={12} height={12} /> : <ChevronRightIcon width={12} height={12} />}
        </span>
        {icon && <span className="af-form-container__accordion-icon">{icon}</span>}
        <div className="af-form-container__accordion-header-content">
          <h4 className="af-form-container__accordion-title">{title}</h4>
          <p className="af-form-container__accordion-subtitle">{subtitle}</p>
        </div>
      </button>
      {isOpen && (
        <div className="af-form-container__accordion-content">
          {children}
        </div>
      )}
    </div>
  );
}

const NAV_CHECKBOXES: Array<{ key: keyof FormContainerConfig; label: string; defaultVal: boolean }> = [
  { key: 'showPreviousButton',    label: 'Show Previous button',    defaultVal: true  },
  { key: 'showNextButton',        label: 'Show Next button',        defaultVal: true  },
  { key: 'showSubmitButton',      label: 'Show Submit button',      defaultVal: true  },
  { key: 'allowSkipSteps',        label: 'Allow skipping steps',    defaultVal: false },
  { key: 'showProgressIndicator', label: 'Show progress indicator', defaultVal: true  },
];

export const FormContainerPropertyPanel: React.FC<FormContainerPropertyPanelProps> = ({
  control,
  onChange,
}) => {
  const [openSections, setOpenSections] = useState<Set<PanelType>>(
    new Set(['basic', 'layout', 'childForms'])
  );
  const [selectedChildIndex, setSelectedChildIndex] = useState(0);

  const toggleSection = (panel: PanelType) => {
    const newOpen = new Set(openSections);
    if (newOpen.has(panel)) {
      newOpen.delete(panel);
    } else {
      newOpen.add(panel);
    }
    setOpenSections(newOpen);
  };

  const config: FormContainerConfig = {
    id: '',
    label: '',
    layout: 'tabs-left',
    validationMode: 'free',
    ...(control.config || {}),
    childForms: control.config?.childForms ?? [],
  };

  const updateConfig = (updates: Partial<FormContainerConfig>) => {
    onChange({ ...control, config: { ...config, ...updates } });
  };

  const updateChildBinding = (field: string, value: any) => {
    onChange({
      ...control,
      config: {
        ...config,
        childForms: config.childForms.map((child: any, i: number) =>
          i === selectedChildIndex
            ? { ...child, dataBinding: { ...child.dataBinding, [field]: value } }
            : child
        ),
      },
    });
  };

  const selectedChild = config.childForms.length > 0
    ? config.childForms[Math.min(selectedChildIndex, config.childForms.length - 1)]
    : undefined;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="af-form-container-property-panel">

      {/* ── Basic ─────────────────────────────────────────────────────────── */}
      <AccordionSection
        title={PANEL_LABELS.basic.title}
        subtitle={PANEL_LABELS.basic.subtitle}
        isOpen={openSections.has('basic')}
        onToggle={() => toggleSection('basic')}
        icon={ContainerBasicIcon ? <ContainerBasicIcon size={16} color="#666" /> : undefined}
      >
        <div className="af-properties-tab__section">
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Container ID
              <input
                type="text"
                className="af-properties-tab__input"
                value={config.id}
                onChange={(e) => updateConfig({ id: e.target.value })}
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Label
              <input
                type="text"
                className="af-properties-tab__input"
                value={config.label}
                onChange={(e) => updateConfig({ label: e.target.value })}
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Description
              <textarea
                className="af-properties-tab__textarea"
                value={config.description || ''}
                onChange={(e) => updateConfig({ description: e.target.value })}
              />
            </label>
          </div>

          <div className="af-properties-tab__checkbox-group">
            <label className="af-properties-tab__checkbox-label">
              <input
                type="checkbox"
                checked={config.hideFormTitles || false}
                onChange={(e) => updateConfig({ hideFormTitles: e.target.checked })}
              />
              Hide form titles
            </label>
          </div>
        </div>
      </AccordionSection>

      {/* ── Layout ────────────────────────────────────────────────────────── */}
      <AccordionSection
        title={PANEL_LABELS.layout.title}
        subtitle={PANEL_LABELS.layout.subtitle}
        isOpen={openSections.has('layout')}
        onToggle={() => toggleSection('layout')}
        icon={ContainerLayoutIcon ? <ContainerLayoutIcon size={16} color="#666" /> : undefined}
      >
        <div className="af-properties-tab__section">
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Layout Type
              <select
                className="af-properties-tab__select"
                value={config.layout}
                onChange={(e) => updateConfig({ layout: e.target.value as any })}
              >
                <option value="tabs-left">Tabs (Left)</option>
                <option value="tabs-top">Tabs (Top)</option>
                <option value="wizard">Wizard</option>
                <option value="accordion">Accordion</option>
              </select>
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Tab Width
              <input
                type="text"
                className="af-properties-tab__input"
                placeholder="200px"
                value={config.layoutConfig?.tabWidth || ''}
                onChange={(e) =>
                  updateConfig({ layoutConfig: { ...config.layoutConfig, tabWidth: e.target.value } })
                }
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Tab Height
              <input
                type="text"
                className="af-properties-tab__input"
                placeholder="auto"
                value={config.layoutConfig?.tabHeight || ''}
                onChange={(e) =>
                  updateConfig({ layoutConfig: { ...config.layoutConfig, tabHeight: e.target.value } })
                }
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Default Active Index
              <input
                type="number"
                className="af-properties-tab__input"
                value={config.layoutConfig?.defaultActiveIndex || 0}
                onChange={(e) =>
                  updateConfig({
                    layoutConfig: { ...config.layoutConfig, defaultActiveIndex: parseInt(e.target.value) },
                  })
                }
              />
            </label>
          </div>
        </div>
      </AccordionSection>

      {/* ── Child Forms ───────────────────────────────────────────────────── */}
      <AccordionSection
        title={PANEL_LABELS.childForms.title}
        subtitle={PANEL_LABELS.childForms.subtitle}
        isOpen={openSections.has('childForms')}
        onToggle={() => toggleSection('childForms')}
        icon={ChildFormsIcon ? <ChildFormsIcon size={16} color="#666" /> : undefined}
      >
        <ChildFormsGridPanel
          control={control}
          onChange={onChange}
          onSelectChild={setSelectedChildIndex}
          selectedIndex={selectedChildIndex}
        />
      </AccordionSection>

      {/* ── Binding ───────────────────────────────────────────────────────── */}
      <AccordionSection
        title={PANEL_LABELS.binding.title}
        subtitle={PANEL_LABELS.binding.subtitle}
        isOpen={openSections.has('binding')}
        onToggle={() => toggleSection('binding')}
        icon={BindingIcon ? <BindingIcon size={16} color="#666" /> : undefined}
      >
        <div className="af-properties-tab__section">
          {selectedChild ? (
            <>
              <p className="af-properties-tab__subsection-title">
                Binding for: <strong>{selectedChild.label}</strong>
              </p>

              <div className="af-properties-tab__group">
                <label className="af-properties-tab__label">
                  Binding Mode
                  <select
                    className="af-properties-tab__select"
                    value={selectedChild.dataBinding.mode}
                    onChange={(e) => updateChildBinding('mode', e.target.value)}
                  >
                    <option value="shared-root">Shared Root</option>
                    <option value="sub-root">Sub Root</option>
                    <option value="independent">Independent</option>
                  </select>
                </label>
              </div>

              {selectedChild.dataBinding.mode === 'sub-root' && (
                <>
                  <div className="af-properties-tab__group">
                    <label className="af-properties-tab__label">
                      Root Path
                      <input
                        type="text"
                        className="af-properties-tab__input"
                        placeholder="e.g., address.personal"
                        value={(selectedChild.dataBinding as any).rootPath || ''}
                        onChange={(e) => updateChildBinding('rootPath', e.target.value)}
                      />
                    </label>
                  </div>

                  <div className="af-properties-tab__checkbox-group">
                    <label className="af-properties-tab__checkbox-label">
                      <input
                        type="checkbox"
                        checked={(selectedChild.dataBinding as any).createRootIfMissing || false}
                        onChange={(e) => updateChildBinding('createRootIfMissing', e.target.checked)}
                      />
                      Create root if missing
                    </label>
                  </div>
                </>
              )}

              <div className="af-properties-tab__group">
                <label className="af-properties-tab__label">
                  Sync Strategy
                  <select
                    className="af-properties-tab__select"
                    value={selectedChild.dataBinding.syncStrategy}
                    onChange={(e) => updateChildBinding('syncStrategy', e.target.value)}
                  >
                    <option value="immediate">Immediate</option>
                    <option value="debounced">Debounced</option>
                    <option value="on-save">On Save</option>
                  </select>
                </label>
              </div>

              {selectedChild.dataBinding.syncStrategy === 'debounced' && (
                <div className="af-properties-tab__group">
                  <label className="af-properties-tab__label">
                    Debounce Delay (ms)
                    <input
                      type="number"
                      className="af-properties-tab__input"
                      value={selectedChild.dataBinding.debounceMs || 500}
                      onChange={(e) => updateChildBinding('debounceMs', parseInt(e.target.value))}
                    />
                  </label>
                </div>
              )}
            </>
          ) : (
            <p className="af-properties-tab__empty-state">Select a child form to configure binding.</p>
          )}
        </div>
      </AccordionSection>

      {/* ── Validation ────────────────────────────────────────────────────── */}
      <AccordionSection
        title={PANEL_LABELS.validation.title}
        subtitle={PANEL_LABELS.validation.subtitle}
        isOpen={openSections.has('validation')}
        onToggle={() => toggleSection('validation')}
        icon={ValidationIcon ? <ValidationIcon size={16} color="#666" /> : undefined}
      >
        <div className="af-properties-tab__section">
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Validation Mode
              <select
                className="af-properties-tab__select"
                value={config.validationMode}
                onChange={(e) => updateConfig({ validationMode: e.target.value as any })}
              >
                <option value="free">Free Navigation</option>
                <option value="blocked">Blocked Navigation</option>
                <option value="indicators">Error Indicators</option>
                <option value="auto-save">Auto Save</option>
              </select>
            </label>
          </div>

          <div className="af-properties-tab__checkbox-group">
            <label className="af-properties-tab__checkbox-label">
              <input
                type="checkbox"
                checked={config.validationConfig?.validateBeforeSwitch || false}
                onChange={(e) =>
                  updateConfig({ validationConfig: { ...config.validationConfig, validateBeforeSwitch: e.target.checked } })
                }
              />
              Validate before switch
            </label>
          </div>

          <div className="af-properties-tab__checkbox-group">
            <label className="af-properties-tab__checkbox-label">
              <input
                type="checkbox"
                checked={config.validationConfig?.showErrorBadges !== false}
                onChange={(e) =>
                  updateConfig({ validationConfig: { ...config.validationConfig, showErrorBadges: e.target.checked } })
                }
              />
              Show error badges
            </label>
          </div>
        </div>
      </AccordionSection>

      {/* ── Save ──────────────────────────────────────────────────────────── */}
      <AccordionSection
        title={PANEL_LABELS.save.title}
        subtitle={PANEL_LABELS.save.subtitle}
        isOpen={openSections.has('save')}
        onToggle={() => toggleSection('save')}
        icon={SaveIcon ? <SaveIcon size={16} color="#666" /> : undefined}
      >
        <div className="af-properties-tab__section">
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Save Target
              <select
                className="af-properties-tab__select"
                value={selectedChild?.dataBinding.saveTarget || 'parent'}
                onChange={(e) => {
                  if (!selectedChild) return;
                  updateChildBinding('saveTarget', e.target.value);
                }}
              >
                <option value="parent">Parent Container</option>
                <option value="api">API Endpoint</option>
                <option value="none">No Save (Read-only)</option>
              </select>
            </label>
          </div>

          {selectedChild?.dataBinding.saveTarget === 'api' && (
            <div className="af-properties-tab__group">
              <label className="af-properties-tab__label">
                API URL
                <input
                  type="text"
                  className="af-properties-tab__input"
                  placeholder="https://api.example.com/save"
                  value={(selectedChild.dataBinding as any).apiUrl || ''}
                  onChange={(e) => updateChildBinding('apiUrl', e.target.value)}
                />
              </label>
            </div>
          )}
        </div>
      </AccordionSection>

      {/* ── Navigation ────────────────────────────────────────────────────── */}
      <AccordionSection
        title={PANEL_LABELS.navigation.title}
        subtitle={PANEL_LABELS.navigation.subtitle}
        isOpen={openSections.has('navigation')}
        onToggle={() => toggleSection('navigation')}
        icon={NavigationIcon ? <NavigationIcon size={16} color="#666" /> : undefined}
      >
        <div className="af-properties-tab__section">
          {NAV_CHECKBOXES.map(({ key, label, defaultVal }) => (
            <div key={key} className="af-properties-tab__checkbox-group">
              <label className="af-properties-tab__checkbox-label">
                <input
                  type="checkbox"
                  checked={config[key] !== undefined ? !!config[key] : defaultVal}
                  onChange={(e) => updateConfig({ [key]: e.target.checked })}
                />
                {label}
              </label>
            </div>
          ))}
        </div>
        </AccordionSection>

    </div>
  );
};

FormContainerPropertyPanel.displayName = 'FormContainerPropertyPanel';
